import { getContentType } from '../../models/enum/content-type.enum';
import { DocumentsService } from '../../services/sections/documents/documents.service';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { takeUntil } from 'rxjs/operators';
import { fadeOut } from 'src/app/animations';
import { Document, DocumentContainerDetails, DocumentContainerType, DocumentGroup } from '../../models/document.model';
import { saveAs } from 'file-saver-es';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { DocViewerDocumentDetails } from '../document-viewer/document-viewer.component';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ToastType } from '../toast/models/toast-type.enum';
import { Toast } from '../toast/models/toast.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import moment from 'moment';

@Component({
	selector: 'app-document-list',
	templateUrl: './document-list.component.html',
	styleUrls: ['./document-list.component.scss', '../stylesheets/document-viewer.scss'],
	animations: [fadeOut(700)]
})
export class DocumentListComponent implements OnInit, OnDestroy, AfterViewInit {

	@ViewChild('sidenav', { static: true }) sidenav: MatSidenav;

	public documents: DocumentGroup[] = [];
	public DocumentContainerType = DocumentContainerType;
	public containerDetails: DocumentContainerDetails = {
		title: null,
		zipDownloadFileName: "documents-archive.zip",
		documentContainerTypeId: DocumentContainerType.Generic
	};
	public isOpened = false;
	public loadingMessage = null;

	public selectedDocumentDetails: DocViewerDocumentDetails = null;

	private unsubscriber$ = new Subject<void>();
	private isScreenMd = window.innerWidth <= 768;

	constructor(
		private route: ActivatedRoute,
		private documentsService: DocumentsService,
		private _sanitizer: DomSanitizer,
	) { }

	public ngOnInit(): void {
		GlobalSettingsService.navigationBarActions.set(
			'download_all_documents',
			{
				action: this.downloadAllZip.bind(this),
				icon: 'cloud_download',
				text: 'Download all',
				listOrder: 1
			}
		);

		this.documents = this.route.snapshot.data.documents;
		this.containerDetails = this.route.snapshot.data.details;

		if (this.documents.length === 1 && this.documents[0].documents.length) {
			this.showDocument(this.documents[0].documents[0].documentId,
				this.documents[0].documents[0].name,
				this.documents[0].documents[0].name);
		}
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.isSecondaryLoaderLoading = false;
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public showDocument(documentId: number, documentName: string, downloadDocumentName: string) {
		this.selectedDocumentDetails = null;

		if (!this.isOpened) {
			this.toggleSlide();
		}

		this.documentsService
			.getDocument(this.route.snapshot.paramMap.get('token'), documentId)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe((content) => {
				this.selectedDocumentDetails = { documentId, content, documentDisplayName: documentName, documentDownloadName: downloadDocumentName};
				this.isOpened = true;
			}, () => {
				this.selectedDocumentDetails = {
					content: null,
					documentDisplayName: null,
					documentDownloadName: null,
					documentId: 0
				};
			});
	}

	public downloadAllZip(): void {
		if (TokenHelper.getToken(this.route)) {
			ToastService.pushToast({
				icon: 'info',
				text: 'Please wait until the zip file is ready...',
				type: ToastType.Info,
				timeout: 10000
			} as Toast);
			this.documentsService.getDocumentsZip(TokenHelper.getToken(this.route)).subscribe(
				(content) => saveAs(content, this.containerDetails.zipDownloadFileName)
			);
		}
	}

	public toggleSlide() {
		this.isOpened = !this.isOpened;
		this.sidenav.toggle();
	}

	public downloadFile(event: UIEvent, documentId: number, fileName: string) {
		event.stopPropagation();
		this.documentsService
			.getDocument(this.route.snapshot.paramMap.get('token'), documentId)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe((content) => {
				saveAs(content, fileName);
			},
				() => console.log('There was an error while downloading')
			);
	}

	public getHeaderClass(document: Document): string {
		const enumName = getContentType(document.contentType).toLowerCase();
		return `mat-card-avatar header-image ${enumName}`;
	}

	public getDocIcon(document: Document): string {
		const enumName = getContentType(document.contentType).toLowerCase();
		return `header-image mat-list-avatar ${enumName}`;
	}

	public getImageThumnail(photo): SafeResourceUrl {
		return this._sanitizer.bypassSecurityTrustResourceUrl(`data:${photo.contentType};base64, ${photo.thumbnailBase64}`);
	}

	private generatorRandomName(): string {
		const stringArr = [];
		for (let i = 0; i < 4; i++) {
			const S4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
			stringArr.push(S4);
		}
		return stringArr.join('-');
	}

	public getName(doc: Document): string {
		if (doc.documentLabel) { return doc.documentLabel; }
		if (!doc.name) { return doc.name; }
		return doc.name.split('.')[0];
	}
	public getDownloadFileName(doc: Document): string {
		return doc.downloadFileName;
	}

	public getDate(doc: Document): string {
		if (!doc.createdOn) { return null; }
		return moment.parseZone(doc.createdOn).format('MM/DD/YYYY');
	}
}
